/**
 * Ce que l'élève voit du live, suivant le mode que pose son masteur et la largeur de l'écran : une
 * seule règle, **en Concentré, l'image du masteur qui bouge ne le distrait pas** (il se tient derrière
 * Sam, en visage rond sous un voile), et **le masteur ne se montre jamais deux fois sur le même écran**
 * (Wajih, 19/10).
 *
 *  - **La visio** (la scène) : au téléphone, une vue à la fois, elle est à l'écran quand l'élève ouvre
 *    « Visio ». Au grand écran, elle est là en Dialogue ; en Concentré, quand l'élève la laisse ouverte —
 *    refermée (« Revenir à l'activité »), sa colonne part à gauche et l'activité prend tout l'écran ; le
 *    visage du masteur dans la bulle la rouvre. L'écran pose la vue sur la visio à l'entrée ; au grand écran,
 *    aussi à chaque changement de mode (Wajih, 22/10 : le masteur se voit à chaque arrivée en Concentré,
 *    derrière Sam). Au téléphone, l'élève reste sur son onglet (Wajih, 24/10 : il quittait son exercice) ;
 *    sur l'activité, la bulle tandem montre Sam qui arrive, et le retour en Dialogue a sa barre.
 *  - **Son partage d'écran** ne se montre pas en Concentré, comme dans l'ancien live, même visio ouverte.
 *    En Dialogue au grand écran, l'élève peut l'agrandir à tout l'écran, l'énoncé masqué (Wajih, 20/10,
 *    comme l'ancien live qui passait la visio en grand pendant un partage) : au téléphone, la visio y
 *    est déjà seule.
 *  - **Le tandem** : visio à l'écran, il s'y tient (`<LiveStage tandem>`) ; sinon, c'est la bulle tandem
 *    en bas à droite, qui porte la caméra du masteur (elle ne se voit nulle part ailleurs).
 *  - **Ma caméra** : sur la scène, ma vignette ; scène hors de l'écran, le bouton caméra de la barre.
 *  - **L'activité** : au grand écran, scène hors de l'écran, une colonne de lecture centrée.
 *
 * C'est une règle d'affichage : le son, la publication et la souscription aux pistes n'en dépendent
 * pas.
 */
import { type UILiveView } from "../../molecules/LiveViewSwitch"
import { type UISoundMode } from "../../molecules/SoundModeBadge"

export type UILiveSight = {
    /** La scène (la visio) est à l'écran. */
    stage: boolean
    /** Le partage d'écran du masteur se montre sur la scène. */
    tutorScreen: boolean
    /** La bulle tandem se pose sur l'activité (la scène n'est pas à l'écran). */
    bubble: boolean
    /** Ma caméra se voit dans le bouton de la barre (la scène n'est pas à l'écran). */
    cameraInBar: boolean
    /** L'activité prend tout l'écran, en colonne de lecture centrée (le grand écran sans la scène). */
    readingColumn: boolean
    /** Le bouton qui agrandit le partage d'écran est offert, pendant un partage. */
    shareToggle: boolean
    /** Le partage agrandi prend tout l'écran : la visio seule, l'énoncé masqué. */
    shareFull: boolean
}

/**
 * `shareFull` : l'élève a agrandi le partage, et le masteur partage encore — l'écran le remet à `false`
 * dès que le partage s'arrête.
 */
export function liveSight(soundMode: UISoundMode, wide: boolean, view: UILiveView, shareFull = false): UILiveSight {
    const focused = soundMode === "focused"
    const shareToggle = wide && !focused
    const full = shareToggle && shareFull
    const stage = full || (wide ? !focused || view === "stage" : view === "stage")
    return {
        stage,
        tutorScreen: !focused,
        bubble: !stage,
        cameraInBar: !stage,
        readingColumn: wide && !stage,
        shareToggle,
        shareFull: full,
    }
}
